import type {
  HostingProviderView,
  HostingProviderPage,
  HostingProviderSummary,
  HostingSupplierPage,
} from '@tosho/contracts';

export class HostingProviderRequestError extends Error {
  public constructor(public readonly code: string) {
    super(code);
  }
}
async function request<T>(
  path: string,
  method = 'GET',
  csrf?: string,
  body?: unknown,
  file?: File,
  version?: number,
): Promise<T> {
  const response = await fetch('/api/hosting-providers' + path, {
    method,
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      ...(csrf === undefined ? {} : { 'X-CSRF-Token': csrf }),
      ...(file
        ? { 'Content-Type': file.type, 'X-Provider-Version': String(version) }
        : body === undefined
          ? {}
          : { 'Content-Type': 'application/json' }),
    },
    ...(file ? { body: file } : body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    const data: unknown = await response.json().catch(() => null);
    const code =
      data && typeof data === 'object' && 'code' in data && typeof data.code === 'string'
        ? data.code
        : response.status === 401 || response.status === 403
          ? 'ACCESS_DENIED'
          : 'HOSTING_PROVIDER_UNAVAILABLE';
    throw new HostingProviderRequestError(code);
  }
  return response.json() as Promise<T>;
}
const pathOf = (provider: HostingProviderSummary): string => '/' + encodeURIComponent(provider.id);
export const hostingProviderApi = {
  list: (q: string, page: number): Promise<HostingProviderPage> =>
    request(`?q=${encodeURIComponent(q)}&page=${page}`),
  sources: (q: string, page: number): Promise<HostingSupplierPage> =>
    request(`/sources?q=${encodeURIComponent(q)}&page=${page}`),
  create: (
    displayName: string,
    sourceReferenceId: string | null,
    csrf: string,
  ): Promise<HostingProviderView> => request('', 'POST', csrf, { displayName, sourceReferenceId }),
  rename: (
    provider: HostingProviderSummary,
    displayName: string,
    csrf: string,
  ): Promise<HostingProviderView> =>
    request(pathOf(provider), 'PATCH', csrf, { displayName, expectedVersion: provider.version }),
  attach: (
    provider: HostingProviderSummary,
    sourceReferenceId: string,
    csrf: string,
  ): Promise<HostingProviderView> =>
    request(pathOf(provider) + '/source', 'POST', csrf, {
      sourceReferenceId,
      expectedVersion: provider.version,
    }),
  saveLogo: (
    provider: HostingProviderSummary,
    file: File,
    csrf: string,
  ): Promise<HostingProviderView> =>
    request(pathOf(provider) + '/logo', 'POST', csrf, undefined, file, provider.version),
  removeLogo: (provider: HostingProviderSummary, csrf: string): Promise<HostingProviderView> =>
    request(pathOf(provider) + '/logo', 'DELETE', csrf, { expectedVersion: provider.version }),
};
export function hostingProviderLogoUrl(provider: HostingProviderSummary): string | null {
  return provider.hasLogo
    ? `/api/hosting-providers/${encodeURIComponent(provider.id)}/logo?v=${provider.version}`
    : null;
}
export function hostingProviderError(error: unknown): string {
  if (error instanceof HostingProviderRequestError) {
    if (error.code === 'HOSTING_PROVIDER_CONFLICT')
      return 'Cette fiche a changé ou ce fournisseur Freebe figure déjà au catalogue. Fermez et actualisez avant de réessayer.';
    if (error.code === 'HOSTING_PROVIDER_INVALID')
      return 'Vérifiez le nom et le fichier : PNG, JPEG ou WebP, 512 Ko maximum.';
    if (error.code === 'ACCESS_DENIED')
      return 'Vous n’avez plus accès aux hébergeurs. Reconnectez-vous.';
  }
  return 'Les hébergeurs ne sont pas disponibles. Actualisez avant de réessayer.';
}
